import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import { LineReveal, Reveal, staggerParent, staggerChild } from "@/lib/motion";
import { Marquee, Overline, PrimaryButton, OutlineButton } from "@/components/common";
import ContactForm from "@/components/ContactForm";
import { COMPANY, IMAGES, ACCREDITATIONS, SERVICE_OVERVIEW, PROOF_POINTS } from "@/data/site";

const HERO_BULLETS = [
  "Industrial refurbishments",
  "Dilapidations",
  "Commercial — offices and retail",
];

const STATS = [
  { value: 40, suffix: "+ yrs", label: "In the building industry" },
  { value: 1986, suffix: "", label: "Established", plain: true },
  { value: 100, suffix: "s", label: "Projects delivered" },
  { value: 35, suffix: "+ yrs", label: "Longest team tenure" },
];

function Counter({ value, suffix, plain }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (plain) {
      setDisplay(value);
      return;
    }
    let start = 0;
    const dur = 1400;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => (start = 0);
  }, [inView, value, plain]);
  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const scrollToForm = () => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden bg-[#0A1128] pb-16 pt-32">
        <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 will-change-transform">
          <img src={IMAGES.hero} alt="Aerial view of an industrial estate refurbished by Andrews Building Company" className="h-[115%] w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/70 to-[#0A1128]/45" />
        <div className="noise z-10" />

        <div className="container-x relative z-20 w-full">
          <h1 className="mt-6 max-w-5xl font-display text-[13vw] font-bold uppercase leading-[0.92] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl">
            <LineReveal
              start
              delay={0.15}
              lines={["Industrial &", "Commercial", "Refurbishment"]}
            />
            <span className="mt-2 block text-[#4b6bd6]">
              <LineReveal start delay={0.5} lines={["Specialists"]} />
            </span>
          </h1>

          <div className="mt-10 grid gap-10 lg:grid-cols-12">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7 }}
              className="speakable max-w-xl text-base leading-relaxed text-slate-300 md:text-lg lg:col-span-7"
            >
              Established in 1986, {COMPANY.name} has delivered reliable, high-quality construction. For nearly 40
              years we've specialised in industrial and commercial refurbishment — a long-standing, hands-on team with
              significant technical expertise, trusted by clients who simply want the job done properly.
            </motion.p>

            <motion.ul
              variants={staggerParent}
              initial="hidden"
              animate="show"
              className="space-y-3 lg:col-span-5"
            >
              {HERO_BULLETS.map((b) => (
                <motion.li key={b} variants={staggerChild} className="flex items-center gap-3 text-sm text-white md:text-base">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-[#1D4ED8]">
                    <Check size={14} className="text-white" />
                  </span>
                  {b}
                </motion.li>
              ))}
            </motion.ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-12 flex flex-col gap-4 sm:flex-row"
          >
            <PrimaryButton onClick={scrollToForm} testId="hero-callback-btn" light>
              Request a Call Back
            </PrimaryButton>
            <OutlineButton onClick={() => navigate("/portfolio")} testId="hero-work-btn" dark>
              View Our Work
            </OutlineButton>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-6 right-6 z-20 hidden items-center gap-2 text-xs uppercase tracking-widest text-slate-400 md:flex"
        >
          <ArrowDown size={16} className="animate-bounce" /> Scroll
        </motion.div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="border-y border-slate-200 py-8">
        <Marquee items={[...ACCREDITATIONS, "Est. 1986"]} />
      </section>

      {/* TRUST STRIP / PROOF */}
      <section className="container-x py-20 md:py-28">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Overline>Trusted for decades</Overline>
            <Reveal>
              <p className="mt-6 max-w-2xl text-balance font-display text-3xl font-medium uppercase leading-tight tracking-tight text-[#0A1128] md:text-4xl">
                All of our work comes from referrals or word of mouth. We're proud of that.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-slate-600">
                We have never felt the need to advertise. Good, dependable work and long-standing relationships have carried
                us for nearly four decades — with a local, responsive team covering {COMPANY.region}.
              </p>
            </Reveal>
          </div>
        </div>

        <motion.div
          variants={staggerParent}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-10%" }}
          className="mt-16 grid grid-cols-2 border-t border-l border-slate-200 md:grid-cols-4"
        >
          {PROOF_POINTS.map((p) => (
            <motion.div key={p.label} variants={staggerChild} className="border-b border-r border-slate-200 p-6 md:p-8">
              <p className="font-display text-4xl font-bold text-[#0A1128] md:text-5xl">{p.value}</p>
              <p className="mt-2 text-xs uppercase tracking-widest text-slate-500">{p.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* MANIFESTO INTRO */}
      <section className="border-t border-slate-200 bg-[#F5F7FA] py-24 md:py-36">
        <div className="container-x">
          <Overline>Not a faceless contractor</Overline>
          <Reveal>
            <blockquote className="mt-8 max-w-5xl text-balance font-display text-3xl font-medium leading-[1.15] tracking-tight text-[#0A1128] sm:text-4xl md:text-5xl">
              &ldquo;We work directly with clients and see every project through personally — solid workmanship, clear
              communication, and projects delivered the right way.&rdquo;
            </blockquote>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-6 font-display text-sm uppercase tracking-[0.2em] text-[#1D4ED8]">— Andrew Chandler, MD</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10">
              <OutlineButton to="/our-story" testId="home-story-btn">
                Read Our Story
              </OutlineButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE OVERVIEW BENTO */}
      <section className="container-x py-24 md:py-32">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Overline>What we do</Overline>
            <h2 className="mt-5 font-display text-4xl font-bold uppercase tracking-tight text-[#0A1128] md:text-5xl">
              Service Overview
            </h2>
          </div>
          <OutlineButton to="/services" testId="home-allservices-btn" className="self-start md:self-auto">
            All Services
          </OutlineButton>
        </div>

        <motion.div
          variants={staggerParent}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-8%" }}
          className="mt-14 grid gap-px bg-slate-200 md:grid-cols-2"
        >
          {SERVICE_OVERVIEW.map((s, i) => (
            <motion.button
              key={s.id}
              variants={staggerChild}
              onClick={() => navigate(`/services#${s.id}`)}
              data-testid={`service-card-${s.id}`}
              className="group relative flex flex-col justify-between bg-white p-8 text-left transition-colors hover:bg-[#0A1128] md:p-12"
            >
              <div className="clip-frame mb-8 aspect-[16/10] w-full">
                <img src={s.image} alt={s.title} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div>
                <span className="font-display text-sm text-slate-400">0{i + 1}</span>
                <h3 className="mt-2 font-display text-2xl font-medium uppercase tracking-tight text-[#0A1128] transition-colors group-hover:text-white md:text-3xl">
                  {s.title}
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 transition-colors group-hover:text-slate-300">
                  {s.blurb}
                </p>
                <span className="link-arrow mt-6 inline-flex items-center gap-2 font-display text-xs uppercase tracking-widest text-[#1D4ED8] transition-colors group-hover:text-[#4b6bd6]">
                  Explore <ArrowRight size={16} />
                </span>
              </div>
            </motion.button>
          ))}
        </motion.div>
      </section>

      {/* ACCREDITATIONS */}
      <section className="border-y border-slate-200 bg-[#F5F7FA] py-20">
        <div className="container-x">
          <Overline>Accreditations & professional bodies</Overline>
          <div className="mt-10 grid grid-cols-2 gap-px bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
            {ACCREDITATIONS.map((a) => (
              <Reveal key={a}>
                <div className="flex h-28 items-center justify-center bg-white p-4 text-center">
                  <span className="font-display text-lg font-medium uppercase tracking-tight text-[#0A1128]">{a}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative overflow-hidden bg-[#1E293B] py-24 md:py-32">
        <div className="noise" />
        <div className="container-x relative">
          <Overline dark>By the numbers</Overline>
          <div className="mt-12 grid grid-cols-2 gap-10 md:grid-cols-4 md:gap-6">
            {STATS.map((s) => (
              <Reveal key={s.label}>
                <p className="font-display text-5xl font-bold text-white md:text-6xl">
                  <Counter value={s.value} suffix={s.suffix} plain={s.plain} />
                </p>
                <p className="mt-3 text-xs uppercase tracking-widest text-slate-400">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact-form" className="scroll-mt-24 py-24 md:py-32">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Overline>Request a call back</Overline>
            <h2 className="mt-5 font-display text-4xl font-bold uppercase leading-none tracking-tighter text-[#0A1128] md:text-6xl">
              Let's talk<br />about the job.
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate-600">
              Tell us what you need and we'll get back to you — usually within one business day. Serving industrial and
              commercial clients across {COMPANY.region}.
            </p>
            <div className="mt-10 space-y-4 border-t border-slate-200 pt-8 text-sm">
              <p><span className="text-slate-500">Email</span><br /><a href={`mailto:${COMPANY.email}`} className="font-medium text-[#0A1128] underline underline-offset-2">{COMPANY.email}</a></p>
              <p><span className="text-slate-500">Phone</span><br /><a href={COMPANY.phoneHref} className="font-medium text-[#0A1128] underline underline-offset-2">{COMPANY.phone}</a></p>
              <p><span className="text-slate-500">Office</span><br /><span className="font-medium text-[#0A1128]">{COMPANY.addressLine}</span></p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}
